/**
 * A reference to globalThis, with support
 * for browsers that don't yet support the spec.
 * @public
 */
export const $global = (function () {
    if (typeof globalThis !== "undefined") {
        // We're running in a modern environment.
        return globalThis;
    }
    if (typeof global !== "undefined") {
        // We're running in NodeJS
        return global;
    }
    if (typeof self !== "undefined") {
        // We're running in a worker.
        return self;
    }
    if (typeof window !== "undefined") {
        // We're running in the browser's main thread.
        return window;
    }
    try {
        // Hopefully we never get here...
        // Not all environments allow eval and Function. Use only as a last resort:
        // eslint-disable-next-line no-new-func
        return new Function("return this")();
    }
    catch {
        // If all fails, give up and create an object.
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        return {};
    }
})();
// API-only Polyfill for trustedTypes
if ($global.trustedTypes === void 0) {
    $global.trustedTypes = { createPolicy: (n, r) => r };
}
// Shims of globals for SSR environments that load client code in the server module graph
// Shim only what is not defined in-case SSR env is loading their own polyfills (which would need to be loaded first).
for (const name of [
    "Node",
    "Element",
    "HTMLElement",
    "ShadowRoot",
    "Event",
    "CustomEvent",
    "MutationObserver",
    "ResizeObserver",
    "IntersectionObserver",
    "CSSStyleSheet"
]) {
    if (typeof ($global[name]) === "undefined") {
        $global[name] = class {
        };
    }
}
// Minimal custom elements registry for the react wrapper to leverage for tag names
if (typeof ($global.customElements) === "undefined") {
    const constructorsByName = new Map();
    const namesByConstructor = new Map();
    $global.customElements = {
        define(name, constructor) {
            if (!constructorsByName.has(name)) {
                constructorsByName.set(name, constructor);
                namesByConstructor.set(constructor, name);
            }
        },
        get(name) {
            return constructorsByName.get(name);
        },
        getName(constructor) {
            return namesByConstructor.get(constructor) ?? null;
        },
        whenDefined() {
            return Promise.resolve();
        },
        upgrade() {
            // no-op
        },
    };
}
const propConfig = {
    configurable: false,
    enumerable: false,
    writable: false,
};
if ($global.FAST === void 0) {
    Reflect.defineProperty($global, "FAST", {
        value: Object.create(null),
        ...propConfig,
    });
}
/**
 * The FAST global.
 * @internal
 */
export const FAST = $global.FAST;
if (FAST.getById === void 0) {
    const storage = Object.create(null);
    Reflect.defineProperty(FAST, "getById", {
        value(id, initialize) {
            let found = storage[id];
            if (found === void 0) {
                found = initialize ? (storage[id] = initialize()) : null;
            }
            return found;
        },
        ...propConfig,
    });
}
/**
 * A readonly, empty array.
 * @remarks
 * Typically returned by APIs that return arrays when there are
 * no actual items to return.
 * @internal
 */
export const emptyArray = Object.freeze([]);
/**
 * Creates a function capable of locating metadata associated with a type.
 * @returns A metadata locator function.
 * @internal
 */
export function createMetadataLocator() {
    const metadataLookup = new WeakMap();
    return function (target) {
        let metadata = metadataLookup.get(target);
        if (metadata === void 0) {
            let currentTarget = Reflect.getPrototypeOf(target);
            while (metadata === void 0 && currentTarget !== null) {
                metadata = metadataLookup.get(currentTarget);
                currentTarget = Reflect.getPrototypeOf(currentTarget);
            }
            metadata = metadata === void 0 ? [] : metadata.slice(0);
            metadataLookup.set(target, metadata);
        }
        return metadata;
    };
}
